import axios from 'axios';
import config from '../config/environment';
import { recaptchaService } from '../utils/recaptchaService';

// City pricing API interfaces
export interface PricingPlan {
  planCode: string;
  basePrice: number;
  gstAmount: number;
  totalAmount: number;
}

export interface ServicePricing {
  serviceId: number;
  serviceName: string;
  serviceDescription: string;
  plans: PricingPlan[];
}

export interface CityPricingResponse {
  status: string;
  cityCode: string;
  cityName: string;
  totalServices: number;
  totalPricingPlans: number;
  services: ServicePricing[];
}

// City information
export interface City {
  code: string;
  name: string;
}

// Common cities list
// NOTE: VARANASI was originally added for at-home service in Varanasi city — not currently offered.
// PLATFORM_SITE represents our Antaryatra Kashi site where Acharyas perform on the devotee's behalf.
export const availableCities: City[] = [
  { code: 'DELHI', name: 'Delhi NCR' },
  { code: 'PLATFORM_SITE', name: 'Kashi – Antaryatra Site' },
  // { code: 'VARANASI', name: 'Varanasi' }, // at-home in Varanasi — not yet available
];

// ---------------------------------------------------------------------------
// Pricing cache key prefix — bump the version whenever DB prices are updated
// so all browsers immediately discard the stale cache and fetch fresh prices.
// Current version: v2 (bumped after GST-inclusive price migration, 2026-07-11)
// ---------------------------------------------------------------------------
export const PRICING_CACHE_PREFIX = 'cityPricing_v2_';

// Create axios instance for pricing API
const pricingApi = axios.create({
  baseURL: config.authServerUrl, // Using same base URL as auth server
  headers: {
    'Content-Type': 'application/json',
  },
});

// Pricing service
export const pricingService = {
  /**
   * Get pricing for all services in a specific city
   */
  getCityPricing: async (cityCode: string): Promise<CityPricingResponse> => {
    try {
      console.log(`🏙️ Fetching pricing for city: ${cityCode}`);
      
      // Generate reCAPTCHA token for pricing API
      const recaptchaToken = await recaptchaService.getToken('get_city_pricing');
      console.log('🛡️ Generated reCAPTCHA token for pricing API');
      
      const response = await pricingApi.post(`/public/pricing/city/${cityCode}/services`, {
        recaptchaToken: recaptchaToken
      });
      
      console.log('✅ City pricing fetched successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching city pricing:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to fetch city pricing';
          throw new Error(`Failed to fetch pricing for ${cityCode}: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach pricing server');
        }
      }
      
      throw new Error(`Failed to fetch pricing for ${cityCode}: ${(error as Error).message}`);
    }
  },

  /**
   * Get pricing for a specific service in a specific city
   */
  getServicePricing: async (cityCode: string, serviceId: number): Promise<ServicePricing | null> => {
    try {
      const cityPricing = await pricingService.getCityPricing(cityCode);
      return cityPricing.services.find(service => service.serviceId === serviceId) || null;
    } catch (error) {
      console.error(`❌ Error fetching pricing for service ${serviceId} in ${cityCode}:`, error);
      throw error;
    }
  },

  /**
   * Get pricing for a specific plan of a service in a city
   */
  getServicePlanPricing: async (cityCode: string, serviceId: number, planCode: string): Promise<PricingPlan | null> => {
    try {
      const servicePricing = await pricingService.getServicePricing(cityCode, serviceId);
      return servicePricing?.plans.find(plan => plan.planCode === planCode) || null;
    } catch (error) {
      console.error(`❌ Error fetching pricing for plan ${planCode} of service ${serviceId} in ${cityCode}:`, error);
      throw error;
    }
  }
};

export default pricingService;